import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { fetchEvents, type EventSummary } from '@features/events/api';
import { formatDateTime } from '@shared/lib/format';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

/** Home widget: the next few upcoming events, tap-through to the events module. */
export function EventsWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['events', 'home-widget'], queryFn: () => fetchEvents() });

  const cutoff = Date.now() - 60 * 60 * 1000; // include events that started within the last hour
  const upcoming = (query.data ?? [])
    .filter((e) => new Date(e.starts_at).getTime() >= cutoff)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .slice(0, 3);

  return (
    <WidgetCard
      title={widgetTitle(config)}
      actionLabel="View all"
      onAction={() => navigate('/app/events')}
      onClick={() => navigate('/app/events')}
    >
      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : upcoming.length === 0 ? (
        <p className="rounded-lg bg-sunken px-4 py-5 text-center text-body-sm text-ink-secondary">
          No upcoming events.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {upcoming.map((event) => (
            <li key={event.event_id}>
              <button
                type="button"
                onClick={() => navigate('/app/events')}
                className="flex w-full flex-col rounded-lg border-l-[3px] border-primary-600 bg-primary-50 px-3 py-1.5 text-left active:scale-[0.99]"
              >
                <span className="truncate text-body-sm font-semibold text-ink">{event.title}</span>
                <span className="truncate text-caption text-ink-secondary">
                  {formatEventLine(event)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

function formatEventLine(event: EventSummary): string {
  const when = formatDateTime(event.starts_at) || '';
  return [when, event.location].filter(Boolean).join(' · ');
}
